
import React from 'react';
import {
    Wallet,
    Plus,
    CircleMinus,
    ArrowRightLeft,
    CreditCard,
    History,
    TrendingUp,
    ChevronRight,
    ArrowUpRight,
    ArrowDownLeft,
    ShieldCheck,
    Zap,
    Coins,
    Copy
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useUserAssets } from '@/shared/hooks/useUserAssets';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useStaking } from '@/shared/hooks/useStaking';
import { useUserETF } from '@/shared/hooks/useUserETF';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';
import { useCryptoPrices } from '@/shared/hooks/useCryptoPrices';
import TradingSuspendedModal from '@/shared/components/TradingSuspendedModal';


const WalletPage: React.FC = () => {
    const { user, profile, loading: authLoading } = useAuth();
    const { status: kycStatus } = useKYC(user?.uid);
    const { assets, loading: assetsLoading, updateBalance, checkAndUpdateDailyStart } = useUserAssets(user?.uid);
    const { myStakings } = useStaking(user?.uid);
    const { positions: etfPositions } = useUserETF(user?.uid);
    const navigate = useNavigate();
    const { prices: livePrices } = useCryptoPrices();

    // 실시간 가격 (업비트 API, 없으면 기본값)
    const liveRates = {
        BTC: livePrices?.BTC?.price || 136_000_000,
        ETH: livePrices?.ETH?.price || 3_600_000,
        USDT: livePrices?.USDT?.price || 1_442,
        BERA: livePrices?.BERA?.price || 7_000, // 실시간 업비트 시세
        TRX: 230
    };

    const [showKRWModal, setShowKRWModal] = React.useState(false);
    const [showCryptoModal, setShowCryptoModal] = React.useState(false);
    const [showSwapModal, setShowSwapModal] = React.useState(false);
    const [showTradingSuspendedModal, setShowTradingSuspendedModal] = React.useState(false);
    const [activeCategory, setActiveCategory] = React.useState<'ALL' | 'ETF' | 'STAKING'>('ALL');

    // Swap States
    const [swapSource, setSwapSource] = React.useState('KRW');
    const [swapTarget, setSwapTarget] = React.useState('BTC');
    const [swapAmount, setSwapAmount] = React.useState<string>('');
    const [swapRate, setSwapRate] = React.useState(136000000); // Default BTC rate

    const [krwTab, setKrwTab] = React.useState<'deposit' | 'withdraw'>('deposit');
    const [cryptoTab, setCryptoTab] = React.useState<'deposit' | 'withdraw'>('deposit');

    // Deposit Form State
    const [depositAmount, setDepositAmount] = React.useState('');

    // Crypto Deposit State
    const [depositCryptoAsset, setDepositCryptoAsset] = React.useState('BTC');
    const [depositCryptoAmount, setDepositCryptoAmount] = React.useState('');

    // Dynamic Wallet Address Logic
    const getDepositAddress = (asset: string) => {
        if (!profile) return 'Loading...';
        switch (asset) {
            case 'BTC': return profile.walletAddressBTC || profile.walletAddress || 'bc1qth8rh8ygp2s9durnjlxc0knhhp0fu8wk7z6wse';
            case 'ETH': return profile.walletAddressETH || '0x3dFbE08Cc996fa279aB9778D554C099FF0178Ee9';
            case 'USDT': return profile.walletAddressUSDT || 'TDDQa549R9cVDdRv6TbbJAMzeBuba4Bop1';
            case 'BERA': return profile.walletAddressBERA || '0xBEra' + (profile?.uid?.slice(0, 8) || '4A9f2c3E').toUpperCase();
            default: return '';
        }
    };
    const depositCryptoAddress = getDepositAddress(depositCryptoAsset);

    // Withdraw Form State
    const [withdrawAsset, setWithdrawAsset] = React.useState('BTC');
    const [withdrawAddress, setWithdrawAddress] = React.useState('');
    const [withdrawAmount, setWithdrawAmount] = React.useState('');

    // KRW Withdrawal Bank Info
    const [withdrawKRWAmount, setWithdrawKRWAmount] = React.useState('');
    const [withdrawBank, setWithdrawBank] = React.useState('국민은행');
    const [withdrawAccountNumber, setWithdrawAccountNumber] = React.useState('');
    const [withdrawAccountHolder, setWithdrawAccountHolder] = React.useState('');

    const [selectedAssetForAction, setSelectedAssetForAction] = React.useState('KRW');

    if (authLoading || (user && assetsLoading) || (user && !assets)) {
        return <div className="min-h-screen flex items-center justify-center">
            <Zap className="h-12 w-12 text-blue-500 animate-spin" />
        </div>;
    }

    const handleTransaction = async (type: string, asset: string, amount: number | string, status: string = 'Pending') => {
        if (!user) return;
        try {
            await addDoc(collection(db, 'transactions'), {
                userId: user.uid,
                userName: user.displayName || user.email?.split('@')[0] || 'User',
                type,
                asset,
                amount: amount.toString(),
                status,
                createdAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error adding transaction: ", error);
        }
    };

    // Safe formatting helper to prevent crashes
    const formatMoney = (value: number | undefined | null) => {
        if (value === undefined || value === null || isNaN(value)) return '0';
        return value.toLocaleString();
    };

    const totalStakedValue = myStakings.filter(s => s.status === 'Active').reduce((acc, s) => acc + s.amount, 0);

    const displayAssets = [
        { name: 'Korean Won', symbol: 'KRW', balance: formatMoney(assets?.krw), value: `₩${formatMoney(assets?.krw)}`, icon: '🇰🇷', change: '0%', isUp: true, currentPrice: null, costBasis: null },
        { name: 'Bitcoin', symbol: 'BTC', balance: assets?.btc?.toString() ?? '0', value: `₩${formatMoney((assets?.btc ?? 0) * liveRates.BTC)}`, icon: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg', change: `${livePrices?.BTC?.change >= 0 ? '+' : ''}${livePrices?.BTC?.change ?? 0}%`, isUp: (livePrices?.BTC?.change ?? 0) >= 0, currentPrice: liveRates.BTC, costBasis: assets?.btcCostBasis ?? null },
        { name: 'Ethereum', symbol: 'ETH', balance: assets?.eth?.toString() ?? '0', value: `₩${formatMoney((assets?.eth ?? 0) * liveRates.ETH)}`, icon: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg', change: `${livePrices?.ETH?.change >= 0 ? '+' : ''}${livePrices?.ETH?.change ?? 0}%`, isUp: (livePrices?.ETH?.change ?? 0) >= 0, currentPrice: liveRates.ETH, costBasis: assets?.ethCostBasis ?? null },
        { name: 'Tether', symbol: 'USDT', balance: assets?.usdt?.toString() ?? '0', value: `₩${formatMoney((assets?.usdt ?? 0) * liveRates.USDT)}`, icon: 'https://cryptologos.cc/logos/tether-usdt-logo.svg', change: `${livePrices?.USDT?.change >= 0 ? '+' : ''}${livePrices?.USDT?.change ?? 0}%`, isUp: (livePrices?.USDT?.change ?? 0) >= 0, currentPrice: liveRates.USDT, costBasis: assets?.usdtCostBasis ?? null },
        { name: 'Berachain', symbol: 'BERA', balance: assets?.bera?.toString() ?? '0', value: `₩${formatMoney((assets?.bera ?? 0) * liveRates.BERA)}`, icon: '🐻', change: `${livePrices?.BERA?.change >= 0 ? '+' : ''}${livePrices?.BERA?.change ?? 0}%`, isUp: (livePrices?.BERA?.change ?? 0) >= 0, currentPrice: liveRates.BERA, costBasis: assets?.beraCostBasis ?? null },
        { name: 'Tron', symbol: 'TRX', balance: assets?.trx?.toString() ?? '0', value: `₩${formatMoney((assets?.trx ?? 0) * liveRates.TRX)}`, icon: 'https://cryptologos.cc/logos/tron-trx-logo.svg', change: '+1.5%', isUp: true, currentPrice: liveRates.TRX, costBasis: assets?.trxCostBasis ?? null },
        { name: 'ETF Investment', symbol: 'ETF', balance: 'Portfolio', value: `₩${formatMoney(assets?.totalEtfValue)}`, icon: '📊', change: 'Manage', isUp: true, currentPrice: null, costBasis: null },
        { name: 'Staking Assets', symbol: 'STK', balance: 'Active', value: `₩${formatMoney(totalStakedValue)}`, icon: '🔒', change: 'Earn APY', isUp: true, currentPrice: null, costBasis: null },
    ];

    const totalEstimatedValue = Math.max(0, assets?.krw ?? 0);

    // 당일 자산 증감 계산
    const dailyStartValue = assets?.dailyStartValue ?? totalEstimatedValue;
    const dailyChange = totalEstimatedValue - dailyStartValue;
    const dailyChangePercent = dailyStartValue > 0 ? ((dailyChange / dailyStartValue) * 100) : 0;
    const isDailyChangePositive = dailyChange >= 0;


    const handleQuickAction = (action: string) => {
        if (action === 'Ledger Logs') navigate('/history');
        if (action === 'Security') navigate('/verification');
        if (action === '나의 코인지갑') {
            setCryptoTab('deposit');
            setShowCryptoModal(true);
        }
        if (action === '원화 입출금') {
            setKrwTab('deposit');
            setShowKRWModal(true);
        }
    };

    const handleAssetClick = (symbol: string) => {
        // 실시간 환율 사용
        const rates: { [key: string]: number } = liveRates;

        if (symbol === 'ETF') {
            setActiveCategory('ETF');
            return;
        }

        if (symbol === 'STK') {
            setActiveCategory('STAKING');
            return;
        }

        // 🚨 거래정지 확인
        if (profile?.tradingSuspended) {
            setShowTradingSuspendedModal(true);
            return;
        }

        if (symbol === 'KRW') {
            // Open Swap: KRW -> Coin (Default BTC)
            setSwapSource('KRW');
            setSwapTarget('BTC');
            setSwapRate(rates['BTC']);
            setSwapAmount('');
            setShowSwapModal(true);
        } else {
            // Open Swap: Coin -> KRW
            setSwapSource(symbol);
            setSwapTarget('KRW');
            setSwapRate(rates[symbol] || 0);
            setSwapAmount('');
            setShowSwapModal(true);
        }
    };

    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-1000 relative">
            <div className="max-w-6xl mx-auto px-4 lg:px-8 space-y-12">

                {/* Header Section */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pt-8">
                    <div className="space-y-4 max-w-2xl">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-500 transition-colors">
                            <Wallet className="h-3.5 w-3.5 fill-blue-500" />
                            <span className="text-[10px] font-black uppercase tracking-[0.2em]">자산 포트폴리오 관리</span>
                        </div>
                        <h1 className="text-3xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                            나의 자산 통합 관리,<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-500 dark:via-indigo-500 dark:to-purple-500">
                                스마트한 포트폴리오
                            </span>
                        </h1>
                        <p className="text-muted-foreground font-bold text-sm md:text-base leading-relaxed max-w-lg uppercase tracking-tight">
                            보유 중인 모든 가상자산과 원화 현황을 한눈에 파악하세요.<br />
                            실시간 시세가 반영된 정확한 가치 평가 정보를 제공합니다.
                        </p>
                    </div>

                    {/* Total Balance Card (Premium Style) */}
                    <div className="p-8 rounded-[2.5rem] bg-card border border-border shadow-2xl relative overflow-hidden group min-w-[320px] transition-colors">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 blur-3xl rounded-full group-hover:bg-blue-500/10 transition-colors"></div>
                        <div className="relative z-10 space-y-6">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic">총 추정 가치</span>
                                <div className="p-2 rounded-xl bg-blue-500/10">
                                    <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-500" />
                                </div>
                            </div>
                            <div>
                                <p className="text-2xl md:text-3xl font-black text-foreground tracking-tighter tabular-nums leading-none">₩{totalEstimatedValue.toLocaleString()}</p>
                            </div>

                            <div className="flex gap-2 pt-2 hidden">
                                {/* Buttons are hidden as per user request */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Actions Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        { icon: Coins, label: '나의 코인지갑', sub: 'MY WALLET', color: 'from-blue-600 to-indigo-600', shadow: 'shadow-blue-600/20' },
                        { icon: ArrowDownLeft, label: '원화 입출금', sub: 'KRW I/O', color: 'from-purple-600 to-fuchsia-600', shadow: 'shadow-purple-600/20' },
                        { icon: History, label: 'Ledger Logs', sub: '거래 기록', color: 'from-amber-600 to-orange-600', shadow: 'shadow-amber-600/20' },
                        { icon: ShieldCheck, label: 'Security', sub: '보안 센터', color: 'from-emerald-600 to-teal-600', shadow: 'shadow-emerald-600/20' },
                    ].map((action, i) => (
                        <button
                            key={i}
                            onClick={() => handleQuickAction(action.label)}
                            className="group p-8 rounded-[3rem] bg-card border border-border hover:border-blue-500/30 transition-all duration-500 shadow-xl flex flex-col items-center gap-5 relative overflow-hidden text-left"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-foreground/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className={cn(
                                "w-16 h-16 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white shadow-2xl transition-transform group-hover:scale-110 duration-500",
                                action.color,
                                action.shadow
                            )}>
                                <action.icon className="h-7 w-7" />
                            </div>
                            <div className="text-center space-y-1">
                                <p className="text-xs font-black text-foreground uppercase tracking-widest">{action.label}</p>
                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-tighter italic">{action.sub}</p>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Asset List Section */}
                <div className="rounded-[3rem] bg-card border border-border overflow-hidden shadow-2xl relative transition-colors">
                    <div className="px-10 py-10 border-b border-border flex flex-col md:flex-row md:items-center justify-between gap-6 bg-accent/30">
                        <div className="space-y-1">
                            <h3 className="font-black flex items-center gap-3 text-foreground text-xl tracking-tighter uppercase">
                                <Coins className="h-6 w-6 text-blue-600 dark:text-blue-500" />
                                {activeCategory === 'ALL' ? '보유 자산 현황' :
                                    activeCategory === 'ETF' ? '보유 ETF 현황' : '스테이킹 상세 현황'}
                            </h3>
                            <p className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.3em]">
                                {activeCategory === 'ALL' ? '기관급 자산 관리 현황표' : '상세 보유 종목 분석 리포트'}
                            </p>
                        </div>
                        {activeCategory !== 'ALL' && (
                            <button
                                onClick={() => setActiveCategory('ALL')}
                                className="px-6 py-2 rounded-xl bg-accent border border-border text-[10px] font-black uppercase tracking-widest hover:bg-foreground hover:text-background transition-all"
                            >
                                전체 목록으로 돌아가기
                            </button>
                        )}
                        <div className="flex gap-2 hidden">
                            {/* Buttons hidden as per user request */}
                        </div>
                    </div>

                    <div className="divide-y divide-border">
                        {activeCategory === 'ALL' && displayAssets.map((asset) => (
                            <div
                                key={asset.symbol}
                                onClick={() => handleAssetClick(asset.symbol)}
                                className="group px-10 py-8 flex items-center justify-between hover:bg-accent/50 transition-all cursor-pointer relative overflow-hidden"
                            >
                                <div className="absolute inset-y-0 left-0 w-1 bg-blue-600 scale-y-0 group-hover:scale-y-100 transition-transform origin-top duration-500"></div>

                                <div className="flex items-center gap-6">
                                    <div className="w-16 h-16 rounded-2xl bg-card border border-border flex items-center justify-center text-3xl overflow-hidden p-3 shadow-inner group-hover:scale-105 transition-transform duration-500">
                                        {asset.icon.startsWith('http') ? (
                                            <img src={asset.icon} className="w-full h-full object-contain filter grayscale dark:grayscale-0 group-hover:grayscale-0 transition-all duration-500" alt={asset.name} />
                                        ) : (
                                            <span className="text-2xl">{asset.icon}</span>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <h4 className="font-black text-foreground text-lg md:text-xl group-hover:text-blue-600 transition-colors uppercase tracking-tight">{asset.name}</h4>
                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-0.5 rounded bg-accent border border-border text-[9px] font-black text-muted-foreground tracking-widest">{asset.symbol}</span>
                                            <span className={cn(
                                                "text-[10px] font-black tracking-tighter",
                                                asset.isUp ? "text-emerald-600 dark:text-emerald-500" : "text-rose-600 dark:text-rose-500"
                                            )}>
                                                {asset.isUp ? '▲' : '▼'} {asset.change} 최근 세션 대비
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-10">
                                    <div className="text-right space-y-1">
                                        <p className="text-xl md:text-2xl font-black text-foreground tracking-tighter tabular-nums leading-none">
                                            {asset.balance} <span className="text-[10px] text-muted-foreground font-black ml-1 uppercase">{asset.symbol}</span>
                                        </p>
                                        <p className="text-[10px] font-black text-muted-foreground tracking-[0.2em] italic uppercase">{asset.value} 상당</p>
                                        {asset.currentPrice !== null && (
                                            <div className="flex items-center justify-end gap-3 mt-1">
                                                <span className="text-[9px] font-black text-blue-500/70 tracking-widest uppercase">
                                                    현재가 ₩{asset.currentPrice.toLocaleString()}
                                                </span>
                                                {asset.costBasis !== null && asset.costBasis > 0 && (
                                                    <span className="text-[9px] font-black text-amber-500/80 tracking-widest uppercase">
                                                        평단가 ₩{asset.costBasis.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-3 rounded-2xl bg-accent text-muted-foreground/30 group-hover:text-blue-600 group-hover:bg-blue-500/10 transition-all duration-500 border border-transparent group-hover:border-blue-500/20">
                                        <ChevronRight className="h-5 w-5" />
                                    </div>
                                </div>
                            </div>
                        ))}

                        {activeCategory === 'ETF' && (
                            etfPositions.length > 0 ? etfPositions.map((pos) => (
                                <div key={pos.id} className="group px-10 py-8 flex items-center justify-between hover:bg-accent/50 transition-all relative overflow-hidden">
                                    <div className="flex items-center gap-6">
                                        <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center text-2xl shadow-inner">
                                            📊
                                        </div>
                                        <div className="space-y-1">
                                            <h4 className="font-black text-foreground text-lg md:text-xl uppercase tracking-tight">{pos.name}</h4>
                                            <div className="flex items-center gap-2">
                                                <span className="px-2 py-0.5 rounded bg-accent border border-border text-[9px] font-black text-muted-foreground tracking-widest">{pos.ticker}</span>
                                                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-500 uppercase tracking-tighter">평단가: ₩{formatMoney(pos.avgPrice)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right space-y-1">
                                        <p className="text-xl md:text-2xl font-black text-foreground tracking-tighter tabular-nums leading-none">
                                            {pos.quantity.toFixed(4)} <span className="text-[10px] text-muted-foreground font-black ml-1 uppercase">UNITS</span>
                                        </p>
                                        <p className="text-[10px] font-black text-muted-foreground tracking-[0.2em] italic uppercase">투자금액: ₩{formatMoney(pos.investedAmount)}</p>
                                    </div>
                                </div>
                            )) : (
                                <div className="px-10 py-20 text-center">
                                    <p className="text-sm font-bold text-muted-foreground uppercase italic mb-6">보유 중인 ETF 상품이 없습니다.</p>
                                    <button onClick={() => navigate('/etf')} className="px-8 py-3 rounded-xl bg-blue-600 text-white font-black text-xs uppercase tracking-widest hover:scale-105 transition-all">ETF 상품 둘러보기</button>
                                </div>
                            )
                        )}

                        {activeCategory === 'STAKING' && (
                            myStakings.filter(s => s.status === 'Active').length > 0 ? myStakings.filter(s => s.status === 'Active').map((stk) => (
                                <div key={stk.id} className="group px-10 py-8 flex items-center justify-between hover:bg-accent/50 transition-all relative overflow-hidden">
                                    <div className="flex items-center gap-6">
                                        <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-2xl shadow-inner">
                                            🔒
                                        </div>
                                        <div className="space-y-1">
                                            <h4 className="font-black text-foreground text-lg md:text-xl uppercase tracking-tight">{stk.productName}</h4>
                                            <div className="flex items-center gap-2">
                                                <span className="px-2 py-0.5 rounded bg-accent border border-border text-[9px] font-black text-muted-foreground tracking-widest">{stk.asset}</span>
                                                <span className="text-[10px] font-black text-blue-600 dark:text-blue-500 uppercase tracking-tighter">연이율(APY): {stk.apy}%</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right space-y-1">
                                        <p className="text-xl md:text-2xl font-black text-foreground tracking-tighter tabular-nums leading-none">
                                            {formatMoney(stk.amount)} <span className="text-[10px] text-muted-foreground font-black ml-1 uppercase">KRW</span>
                                        </p>
                                        <p className="text-[10px] font-black text-muted-foreground tracking-[0.2em] italic uppercase">스테이킹 시작: {new Date(stk.stakedAt.seconds * 1000).toLocaleDateString()}</p>
                                    </div>
                                </div>
                            )) : (
                                <div className="px-10 py-20 text-center">
                                    <p className="text-sm font-bold text-muted-foreground uppercase italic mb-6">진행 중인 스테이킹이 없습니다.</p>
                                    <button onClick={() => navigate('/staking')} className="px-8 py-3 rounded-xl bg-emerald-600 text-white font-black text-xs uppercase tracking-widest hover:scale-105 transition-all">스테이킹 상품 알아보기</button>
                                </div>
                            )
                        )}
                    </div>

                    {/* Sync Info Footer */}
                    <div className="p-10 bg-accent/10 flex flex-col items-center justify-center gap-4">
                        <Zap className="h-6 w-6 text-blue-500/30 animate-pulse" />
                        <p className="text-[10px] font-black text-muted-foreground/30 uppercase tracking-[0.4em]">코인투캐쉬 고성능 자산 증명 및 정산 시스템 작동 중</p>
                    </div>
                </div>
            </div>

            {/* KRW Deposit/Withdraw Modal */}
            {showKRWModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
                    <div className="w-full max-w-md bg-card rounded-[2rem] border border-border/50 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-6 border-b border-border/50 flex items-center justify-between">
                            <h3 className="text-lg font-black uppercase tracking-tight">원화 입출금 (KRW)</h3>
                            <button onClick={() => setShowKRWModal(false)} className="p-2 rounded-full hover:bg-accent transition-colors">
                                <span className="sr-only">Close</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-border/50">
                            <button
                                onClick={() => setKrwTab('deposit')}
                                className={cn("flex-1 py-4 text-sm font-black uppercase tracking-widest transition-colors", krwTab === 'deposit' ? "text-blue-600 border-b-2 border-blue-600 bg-blue-500/5" : "text-muted-foreground hover:bg-accent/50")}
                            >
                                원화 입금
                            </button>
                            <button
                                onClick={() => setKrwTab('withdraw')}
                                className={cn("flex-1 py-4 text-sm font-black uppercase tracking-widest transition-colors", krwTab === 'withdraw' ? "text-rose-600 border-b-2 border-rose-600 bg-rose-500/5" : "text-muted-foreground hover:bg-accent/50")}
                            >
                                원화 출금
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            {krwTab === 'deposit' ? (
                                <>
                                    <div className="text-center space-y-2">
                                        <div className="mx-auto w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
                                            <ArrowDownLeft className="h-8 w-8 text-blue-600" />
                                        </div>
                                        <p className="text-sm font-bold text-muted-foreground">입금하실 금액을 입력해 주세요.</p>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">입금 금액 (KRW)</label>
                                            <input
                                                type="number"
                                                placeholder="0"
                                                value={depositAmount}
                                                onChange={(e) => setDepositAmount(e.target.value)}
                                                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-blue-500 font-bold"
                                            />
                                        </div>
                                    </div>
                                    <button onClick={async () => {
                                        if (!user) {
                                            alert('로그인이 필요합니다.');
                                            return;
                                        }
                                        const amount = parseFloat(depositAmount);
                                        if (!amount || amount <= 0) {
                                            alert('입금 금액을 입력해주세요.');
                                            return;
                                        }

                                        try {
                                            // Firestore에 입금 신청 기록
                                            await addDoc(collection(db, 'transactionRequests'), {
                                                userId: user.uid,
                                                userName: user.displayName || user.email?.split('@')[0] || 'User',
                                                type: 'deposit',
                                                currency: 'KRW',
                                                amount: amount,
                                                status: 'pending', // 계좌 발급 대기
                                                createdAt: serverTimestamp(),
                                                updatedAt: serverTimestamp()
                                            });

                                            setShowKRWModal(false);
                                            setDepositAmount('');
                                            alert('입금 신청이 접수되었습니다.\n관리자 확인 후 가상계좌가 발급됩니다.');
                                        } catch (error) {
                                            console.error('입금 신청 실패:', error);
                                            alert('입금 신청 중 오류가 발생했습니다.');
                                        }
                                    }} className="w-full py-4 rounded-xl bg-blue-600 text-white font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-blue-600/20">
                                        입금 신청하기
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="text-center space-y-2">
                                        <div className="mx-auto w-16 h-16 rounded-full bg-rose-500/10 flex items-center justify-center mb-4">
                                            <ArrowUpRight className="h-8 w-8 text-rose-600" />
                                        </div>
                                        <p className="text-sm font-bold text-muted-foreground">출금할 계좌 정보를 입력하세요.</p>
                                        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-600 font-bold text-[11px]">
                                            출금 가능 금액: ₩{formatMoney(assets?.krw)}
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">출금 금액 (KRW)</label>
                                            <input
                                                type="number"
                                                placeholder="출금할 금액을 입력하세요"
                                                value={withdrawKRWAmount}
                                                onChange={(e) => setWithdrawKRWAmount(e.target.value)}
                                                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-rose-500 font-bold"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">은행</label>
                                            <select
                                                value={withdrawBank}
                                                onChange={(e) => setWithdrawBank(e.target.value)}
                                                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-rose-500 font-bold"
                                            >
                                                <option value="국민은행">국민은행</option>
                                                <option value="신한은행">신한은행</option>
                                                <option value="우리은행">우리은행</option>
                                                <option value="하나은행">하나은행</option>
                                                <option value="농협은행">농협은행</option>
                                            </select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">계좌번호</label>
                                            <input
                                                type="text"
                                                placeholder="계좌번호를 입력하세요"
                                                value={withdrawAccountNumber}
                                                onChange={(e) => setWithdrawAccountNumber(e.target.value)}
                                                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-rose-500 font-bold"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">예금주</label>
                                            <input
                                                type="text"
                                                placeholder="예금주명을 입력하세요"
                                                value={withdrawAccountHolder}
                                                onChange={(e) => setWithdrawAccountHolder(e.target.value)}
                                                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-rose-500 font-bold"
                                            />
                                        </div>
                                    </div>

                                    <button onClick={async () => {
                                        if (!user) {
                                            alert('로그인이 필요합니다.');
                                            return;
                                        }

                                        // ===== Advanced KYC (Level 2) 검증 =====
                                        if (!kycStatus || kycStatus.level < 2) {
                                            if (window.confirm('[출금 불가] 출금을 위해 고급 인증이 필요합니다.\n\n인증 페이지로 이동하시겠습니까?')) {
                                                navigate('/verification');
                                            }
                                            return;
                                        }
                                        // =========================================

                                        // ===== 출금 정지 확인 =====
                                        if (profile?.withdrawalSuspended) {
                                            alert('[출금 불가] 출금이 정지된 계정입니다.\n관리자에게 문의하세요.');
                                            return;
                                        }
                                        // ========================

                                        const amount = parseFloat(withdrawKRWAmount);
                                        if (!amount || amount <= 0) {
                                            alert('올바른 금액을 입력해주세요.');
                                            return;
                                        }

                                        // 잔액 확인
                                        const currentBalance = assets?.krw || 0;
                                        if (amount > currentBalance) {
                                            alert(`잔액이 부족합니다.\n현재 잔액: ₩${currentBalance.toLocaleString()}\n출금 요청: ₩${amount.toLocaleString()}`);
                                            return;
                                        }

                                        if (!withdrawAccountNumber || !withdrawAccountHolder) {
                                            alert('계좌 정보를 모두 입력해주세요.');
                                            return;
                                        }

                                        try {
                                            await addDoc(collection(db, 'transactionRequests'), {
                                                userId: user.uid,
                                                userName: user.displayName || user.email?.split('@')[0] || 'User',
                                                type: 'withdrawal',
                                                currency: 'KRW',
                                                amount: amount,
                                                withdrawalBankInfo: {
                                                    bank: withdrawBank,
                                                    accountNumber: withdrawAccountNumber,
                                                    accountHolder: withdrawAccountHolder
                                                },
                                                status: 'pending',
                                                createdAt: serverTimestamp(),
                                                updatedAt: serverTimestamp()
                                            });

                                            setShowKRWModal(false);
                                            setWithdrawKRWAmount('');
                                            setWithdrawAccountNumber('');
                                            setWithdrawAccountHolder('');
                                            alert('출금 신청이 접수되었습니다.\n관리자 승인 후 처리됩니다.');
                                        } catch (error) {
                                            console.error('출금 신청 실패:', error);
                                            alert('출금 신청 중 오류가 발생했습니다.');
                                        }
                                    }} className="w-full py-4 rounded-xl bg-rose-600 text-white font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-rose-600/20">
                                        출금 신청하기
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* My Coin Wallet Modal (Simplified) */}
            {showCryptoModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
                    <div className="w-full max-w-md bg-card rounded-[2rem] border border-border/50 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-6 border-b border-border/50 flex items-center justify-between">
                            <h3 className="text-lg font-black uppercase tracking-tight">나의 코인지갑 (Crypto Wallet)</h3>
                            <button onClick={() => setShowCryptoModal(false)} className="p-2 rounded-full hover:bg-accent transition-colors">
                                <span className="sr-only">Close</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Mode Tabs: Deposit / Withdraw */}
                        <div className="flex border-b border-border/50">
                            <button
                                onClick={() => setCryptoTab('deposit')}
                                className={cn("flex-1 py-4 text-xs font-black uppercase tracking-widest transition-colors", cryptoTab === 'deposit' ? "text-blue-600 border-b-2 border-blue-600 bg-blue-500/5" : "text-muted-foreground hover:bg-accent/50")}
                            >
                                코인 입금
                            </button>
                            <button
                                onClick={() => setCryptoTab('withdraw')}
                                className={cn("flex-1 py-4 text-xs font-black uppercase tracking-widest transition-colors", cryptoTab === 'withdraw' ? "text-rose-600 border-b-2 border-rose-600 bg-rose-500/5" : "text-muted-foreground hover:bg-accent/50")}
                            >
                                코인 출금
                            </button>
                        </div>

                        <div className="p-0 pb-8 space-y-6 flex flex-col items-center">
                            {/* Coin Selector Tabs */}
                            <div className="w-full flex border-b border-border/50 bg-accent/5">
                                {[
                                    { coin: 'BTC', logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg' },
                                    { coin: 'ETH', logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg' },
                                    { coin: 'USDT', logo: 'https://cryptologos.cc/logos/tether-usdt-logo.svg' },
                                    { coin: 'BERA', logo: null },
                                ].map(({ coin, logo }) => (
                                    <button
                                        key={coin}
                                        onClick={() => {
                                            setDepositCryptoAsset(coin);
                                            setWithdrawAsset(coin);
                                        }}
                                        className={cn(
                                            "flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-all flex flex-col items-center gap-1",
                                            depositCryptoAsset === coin
                                                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-500/10"
                                                : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                                        )}
                                    >
                                        {logo ? (
                                            <img src={logo} alt={coin} className="w-5 h-5 object-contain" />
                                        ) : (
                                            <span className="text-lg leading-none">🐻</span>
                                        )}
                                        <span>{coin}</span>
                                    </button>
                                ))}
                            </div>

                            {cryptoTab === 'deposit' ? (
                                <div className="px-8 w-full space-y-8 flex flex-col items-center">
                                    {/* QR Code Section */}
                                    <div className="relative group">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                                        <div className="relative p-6 bg-white rounded-[1.8rem] shadow-xl">
                                            <img
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${depositCryptoAddress}`}
                                                alt="Wallet QR Code"
                                                className="w-44 h-44 md:w-48 md:h-48 object-contain"
                                            />
                                        </div>
                                    </div>

                                    {/* Address Section */}
                                    <div className="w-full space-y-3 text-center">
                                        <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">My {depositCryptoAsset} Address</p>
                                        <div className="p-4 rounded-xl bg-accent/50 border border-border flex items-center justify-between gap-3 group hover:border-blue-500/30 transition-colors">
                                            <p className="font-mono text-[11px] md:text-xs font-bold text-foreground break-all text-center flex-1">
                                                {depositCryptoAddress}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(depositCryptoAddress);
                                                alert('지갑 주소가 복사되었습니다.');
                                            }}
                                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-widest shadow-lg shadow-blue-600/20 active:scale-95 transition-all"
                                        >
                                            <Copy className="w-3.5 h-3.5" />
                                            Address Copy
                                        </button>
                                    </div>

                                    <p className="text-[10px] font-bold text-muted-foreground text-center max-w-[90%] leading-relaxed">
                                        위 주소는 고객님의 고유 {depositCryptoAsset} 지갑 주소입니다.<br />
                                        해당 자산을 정확히 확인 후 송금해 주시기 바랍니다.
                                    </p>
                                </div>
                            ) : (
                                <div className="px-8 w-full space-y-6">
                                    <div className="text-center space-y-2">
                                        <div className="mx-auto w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center">
                                            <ArrowUpRight className="h-6 w-6 text-rose-600" />
                                        </div>
                                        <p className="text-sm font-bold text-muted-foreground uppercase">코인 출금 신청</p>
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-600 font-bold text-[10px] uppercase">
                                            {withdrawAsset} Balance: {assets?.[withdrawAsset.toLowerCase() as keyof typeof assets] || 0}
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">출금 주소 (Wallet Address)</label>
                                            <input
                                                type="text"
                                                placeholder={`${withdrawAsset} 지갑 주소를 입력하세요`}
                                                value={withdrawAddress}
                                                onChange={(e) => setWithdrawAddress(e.target.value)}
                                                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-rose-500 font-bold text-xs"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">출금 수량 (Amount)</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    placeholder="0.00000000"
                                                    value={withdrawAmount}
                                                    onChange={(e) => setWithdrawAmount(e.target.value)}
                                                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-rose-500 font-bold text-xs pr-16"
                                                />
                                                <button
                                                    onClick={() => setWithdrawAmount(String(assets?.[withdrawAsset.toLowerCase() as keyof typeof assets] || 0))}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-rose-600 hover:underline"
                                                >
                                                    MAX
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-4 rounded-xl bg-accent/30 border border-border border-dashed space-y-2">
                                        <div className="flex justify-between text-[10px] font-bold text-muted-foreground uppercase">
                                            <span>출금 수수료 (Fee)</span>
                                            <span>Network Dependent</span>
                                        </div>
                                        <p className="text-[9px] text-rose-500 font-bold leading-tight uppercase italic opacity-70">
                                            * 지갑 주소 오기입으로 인한 자산 손실은 복구가 불가능합니다.<br />
                                            * 반드시 해당 코인의 네트워크 규격을 확인하세요.
                                        </p>
                                    </div>

                                    <button onClick={async () => {
                                        if (!user) {
                                            alert('로그인이 필요합니다.');
                                            return;
                                        }

                                        // ===== Advanced KYC (Level 2) 검증 =====
                                        if (!kycStatus || kycStatus.level < 2) {
                                            if (window.confirm('[출금 불가] 출금을 위해 고급 인증이 필요합니다.\n\n인증 페이지로 이동하시겠습니까?')) {
                                                navigate('/verification');
                                            }
                                            return;
                                        }
                                        // =========================================

                                        if (profile?.withdrawalSuspended) {
                                            alert('[출금 불가] 출금이 정지된 계정입니다.\n관리자에게 문의하세요.');
                                            return;
                                        }

                                        const amount = parseFloat(withdrawAmount);
                                        const currentBal = assets?.[withdrawAsset.toLowerCase() as keyof typeof assets] as number || 0;

                                        if (!amount || amount <= 0) {
                                            alert('출금 수량을 입력해주세요.');
                                            return;
                                        }
                                        if (amount > currentBal) {
                                            alert('보유 잔액이 부족합니다.');
                                            return;
                                        }
                                        if (!withdrawAddress.trim()) {
                                            alert('출금 주소를 입력해주세요.');
                                            return;
                                        }

                                        try {
                                            await addDoc(collection(db, 'transactionRequests'), {
                                                userId: user.uid,
                                                userName: user.displayName || user.email?.split('@')[0] || 'User',
                                                type: 'withdrawal',
                                                currency: withdrawAsset,
                                                amount: amount,
                                                withdrawalAddress: withdrawAddress,
                                                status: 'pending',
                                                createdAt: serverTimestamp(),
                                                updatedAt: serverTimestamp()
                                            });

                                            setShowCryptoModal(false);
                                            setWithdrawAmount('');
                                            setWithdrawAddress('');
                                            alert(`${withdrawAsset} 출금 신청이 접수되었습니다.\n관리자 승인 후 처리됩니다.`);
                                        } catch (error) {
                                            console.error('출금 신청 실패:', error);
                                            alert('출금 신청 중 오류가 발생했습니다.');
                                        }
                                    }} className="w-full py-4 rounded-xl bg-rose-600 text-white font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-rose-600/20">
                                        출금 신청 (Request Withdrawal)
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Swap Modal */}
            {showSwapModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
                    <div className="w-full max-w-md bg-card rounded-[2rem] border border-border/50 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-6 border-b border-border/50 flex items-center justify-between">
                            <h3 className="text-lg font-black uppercase tracking-tight">자산 스왑 (Asset Swap)</h3>
                            <button onClick={() => setShowSwapModal(false)} className="p-2 rounded-full hover:bg-accent transition-colors">
                                <span className="sr-only">Close</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-8 space-y-6">
                            {/* Source Asset Input */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">
                                        {swapSource} 수량 (보내기)
                                    </label>
                                    <span className="text-[10px] font-bold text-blue-600 cursor-pointer hover:underline" onClick={() => {
                                        const bal = swapSource === 'KRW' ? (assets?.krw || 0) : (assets?.[swapSource.toLowerCase() as keyof typeof assets] as number || 0);
                                        setSwapAmount(bal.toString());
                                    }}>
                                        보유: {formatMoney(swapSource === 'KRW' ? (assets?.krw || 0) : (assets?.[swapSource.toLowerCase() as keyof typeof assets] as number || 0))} {swapSource}
                                    </span>
                                </div>
                                <div className="relative">
                                    <input
                                        type="number"
                                        value={swapAmount}
                                        onChange={(e) => setSwapAmount(e.target.value)}
                                        placeholder="0"
                                        className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:border-blue-500 font-bold pr-24"
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                        <button
                                            onClick={() => {
                                                const bal = swapSource === 'KRW' ? (assets?.krw || 0) : (assets?.[swapSource.toLowerCase() as keyof typeof assets] as number || 0);
                                                setSwapAmount(bal.toString());
                                            }}
                                            className="px-2 py-1 rounded-md bg-accent text-[10px] font-black text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                                        >
                                            전액
                                        </button>
                                        <span className="text-xs font-black text-muted-foreground">{swapSource}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Swap Icon */}
                            <div className="flex justify-center">
                                <div className="p-2 rounded-full bg-accent text-blue-600">
                                    <ArrowRightLeft className="h-4 w-4" />
                                </div>
                            </div>

                            {/* Calculations */}
                            {(() => {
                                const amount = parseFloat(swapAmount || '0');
                                const rates: { [key: string]: number } = liveRates;

                                let convertedValue = 0;
                                let fee = 0;
                                let finalReceive = 0;
                                let targetSymbol = swapTarget;

                                if (swapSource === 'KRW') {
                                    // KRW -> Coin
                                    // Rate is 1 Coin = X KRW
                                    // So, Coin Amount = KRW Amount / Rate
                                    const currentRate = rates[swapTarget] || 1;
                                    const grossCoin = amount / currentRate;
                                    fee = grossCoin * 0.03; // 3% fee
                                    finalReceive = grossCoin - fee;
                                    targetSymbol = swapTarget;
                                } else {
                                    // Coin -> KRW
                                    // Rate is 1 Coin = X KRW
                                    // KRW Amount = Coin Amount * Rate
                                    const currentRate = rates[swapSource] || 0;
                                    const grossKRW = amount * currentRate;
                                    fee = grossKRW * 0.03; // 3% fee
                                    finalReceive = grossKRW - fee;
                                    targetSymbol = 'KRW';
                                }

                                return (
                                    <div className="space-y-4 p-4 rounded-xl bg-accent/30 border border-border/50">
                                        {/* Coin Selection (Only if KRW -> Coin) */}
                                        {swapSource === 'KRW' && (
                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">
                                                    스왑 대상 코인
                                                </label>
                                                <select
                                                    value={swapTarget}
                                                    onChange={(e) => {
                                                        setSwapTarget(e.target.value);
                                                        setSwapRate(rates[e.target.value]);
                                                    }}
                                                    className="w-full px-4 py-2 rounded-lg bg-background border border-border text-sm font-bold focus:outline-none"
                                                >
                                                    <option value="BTC">Bitcoin (BTC)</option>
                                                    <option value="ETH">Ethereum (ETH)</option>
                                                    <option value="USDT">Tether (USDT)</option>
                                                </select>
                                            </div>
                                        )}

                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground font-bold">적용 환율 (실시간)</span>
                                            <span className="font-mono text-blue-500">
                                                {swapSource === 'KRW'
                                                    ? `1 ${swapTarget} = ₩${(rates[swapTarget] || 0).toLocaleString()}`
                                                    : `1 ${swapSource} = ₩${(rates[swapSource] || 0).toLocaleString()}`
                                                }
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-xs text-rose-500 font-bold">
                                            <span>스왑 수수료 (3%)</span>
                                            <span>- {swapSource === 'KRW' ? fee.toFixed(6) : Math.floor(fee).toLocaleString()} {targetSymbol}</span>
                                        </div>
                                        <div className="pt-2 border-t border-border/50 flex justify-between items-center text-sm font-black">
                                            <span>최종 수령액 (받기)</span>
                                            <span className="text-blue-600 dark:text-blue-400">
                                                {swapSource === 'KRW' ? finalReceive.toFixed(6) : Math.floor(finalReceive).toLocaleString()} {targetSymbol}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}

                            <button onClick={async () => {
                                if (!user) {
                                    alert('로그인이 필요합니다.');
                                    return;
                                }
                                if (!swapAmount || parseFloat(swapAmount) <= 0) {
                                    alert('수량을 입력해주세요.');
                                    return;
                                }

                                const amountVal = parseFloat(swapAmount);
                                const currentBal = swapSource === 'KRW' ? (assets?.krw || 0) : (assets?.[swapSource.toLowerCase() as keyof typeof assets] as number || 0);

                                if (amountVal > currentBal) {
                                    alert('보유 잔액이 부족합니다.');
                                    return;
                                }

                                // 실시간 환율로 재계산
                                const rates: { [key: string]: number } = liveRates;
                                let finalReceive = 0;
                                let targetSymbol = swapTarget;
                                if (swapSource === 'KRW') {
                                    const currentRate = rates[swapTarget] || 1;
                                    const grossCoin = amountVal / currentRate;
                                    const fee = grossCoin * 0.03;
                                    finalReceive = grossCoin - fee;
                                    targetSymbol = swapTarget;
                                } else {
                                    const currentRate = rates[swapSource] || 0;
                                    const grossKRW = amountVal * currentRate;
                                    const fee = grossKRW * 0.03;
                                    finalReceive = grossKRW - fee;
                                    targetSymbol = 'KRW';
                                }

                                // Execute Swap
                                try {
                                    if (swapSource === 'KRW') {
                                        // KRW -> Coin
                                        await updateBalance('krw', -amountVal);
                                        await updateBalance(swapTarget.toLowerCase() as any, finalReceive);
                                    } else {
                                        // Coin -> KRW
                                        await updateBalance(swapSource.toLowerCase() as any, -amountVal);
                                        await updateBalance('krw', finalReceive);
                                    }

                                    // Record Transaction
                                    await handleTransaction(
                                        'SWAP',
                                        `${swapSource} → ${targetSymbol}`,
                                        amountVal,
                                        'COMPLETED'
                                    );

                                    // 로그 기록 추가
                                    await addDoc(collection(db, 'transactions'), {
                                        userId: user.uid,
                                        userName: user?.displayName || user?.email?.split('@')[0] || 'User',
                                        type: 'SWAP',
                                        asset: swapSource === 'KRW' ? `KRW → ${swapTarget}` : `${swapSource} → KRW`,
                                        amount: swapSource === 'KRW' ? `₩${formatMoney(amountVal)}` : `${amountVal} ${swapSource}`,
                                        status: 'COMPLETED',
                                        timestamp: serverTimestamp()
                                    });

                                    setShowSwapModal(false);
                                    setSwapAmount('');
                                    alert(`스왑이 완료되었습니다.\n수수료 3%가 차감된 금액으로 교환되었습니다.`);
                                } catch (e) {
                                    console.error(e);
                                    alert('스왑 중 오류가 발생했습니다.');
                                }
                            }} className="w-full mt-4 py-4 rounded-xl bg-blue-600 text-white font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-blue-600/20">
                                스왑 실행 (Swap Now)
                            </button>


                        </div>
                    </div>
                </div>
            )
            }

            {/* 거래정지 모달 */}
            {showTradingSuspendedModal && (
                <TradingSuspendedModal onClose={() => setShowTradingSuspendedModal(false)} />
            )}
        </div>
    );
};

export default WalletPage;
